import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy, ChangeDetectorRef, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { Subscription, finalize, forkJoin } from 'rxjs';
import { BlahService } from '@headpower/blah-ng';
import { ObservationsService } from './observations.service';
import { Observation, ObservationAttachment, ObservationMetadata } from './models/observation.model';

@Component({
  selector: 'app-observation',
  templateUrl: './observation.component.html',
  styleUrls: ['./observation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationComponent implements OnInit, OnChanges, OnDestroy {

  @Input() mobile: boolean;
  @Input() observationId: string;

  @Output() taskLink: EventEmitter<{ Id: string, TaskId: string, Identifier: string, Name: string }> = new EventEmitter(undefined);
  @Output() updated: EventEmitter<any> = new EventEmitter();

  public selectedObservation: Observation;

  private subscriptions = Array<Subscription>();

  public classifications = [0, 1, 2, 3, 4, 5, 6];

  public classification: number;

  public imageArchiveVisible: boolean = false;

  public loadingImages: boolean = false;

  public attachedTask: { Id: string, TaskId: string, Identifier: string, Name: string } = null;

  constructor(private cdRef: ChangeDetectorRef,
    private observationService: ObservationsService,
    private blahService: BlahService) {
  }

  ngOnInit() {

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.observationId) {
      if (this.observationId) {
        this.observationService.getObservation(this.observationId)
          .subscribe(res => {
            this.selectedObservation = res;

            if (this.selectedObservation) {
              this.observationService.attachedToTask(this.selectedObservation.TaskId)
                .subscribe(res => {
                  this.attachedTask = res;
                  this.taskLink.emit(this.attachedTask);
                })
            }

            this.cdRef.markForCheck();
            this.loadImages();
          });
      }
      else {
        this.selectedObservation = null;
        this.attachedTask = undefined;
        this.taskLink.emit(this.attachedTask);
        this.cdRef.markForCheck();
      }

    }
  }


  private loadImages(): void {
    if (this.selectedObservation.Attachments) {
      this.loadingImages = true;

      var requests = this.selectedObservation.Attachments.map(attachment => {
        return this.observationService.getImage(this.selectedObservation.Id, attachment.Id);
      });

      forkJoin(requests)
        .pipe(finalize(() => {
          this.loadingImages = false;
        }))
        .subscribe(res => {
          for (var i in res) {
            const temp = this.selectedObservation.Attachments[i];
            if (res[i].body) {
              const as_blob = res[i].body as Blob;
              const reader = new FileReader();
              reader.addEventListener('load', () => {
                temp.ImageUrl = reader.result;
                this.cdRef.markForCheck();
              }, false);
              if (as_blob) {
                reader.readAsDataURL(as_blob);
              }
            }
          }
        })
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(o => o.unsubscribe());
  }

  public getSize(): Number {
    const sizeAsNumber = parseFloat(this.selectedObservation.Size);
    return isNaN(sizeAsNumber) || !sizeAsNumber ? null : sizeAsNumber;
  }

  public handleClassificationSelect(selection: any) {
    if (this.selectedObservation.Validity !== selection) {
      this.selectedObservation.Validity = selection;
      this.save();
    }
  }

  public displayImageHeader(attachmentIndex: number): boolean {
    if (attachmentIndex === 0) {
      return true;
    }

    let type = this.selectedObservation.Attachments[attachmentIndex];

    let previousIndex = attachmentIndex - 1;
    let previousType = this.selectedObservation.Attachments[previousIndex];

    // if all are not visible, compare to latest visible
    while (previousIndex > 0 && !this.imageArchiveVisible && previousType.Archived) {
      previousIndex = previousIndex - 1;

      previousType = this.selectedObservation.Attachments[previousIndex];
    }

    if (previousType === undefined) {
      return false;
    }

    return type.Type !== previousType.Type;
  }

  public getMetadataValue(metadata: ObservationMetadata): string {
    let key = metadata.Key;

    if (key == 'main_tree_species' || key == 'tree_species') {
      return this.blahService.blah('satnet.metadata.treeSpecies.' + metadata.Value);
    }

    return metadata.Value;
  }


  public getHeaderName(type: string): string {
    switch (type) {
      case 'rgb':
        return 'Std';

      case 'rgb_hr':
        return 'Hr';

      case 'rgb_vhr':
        return 'Vhr';

      case 'nir':
        return 'NIR';

      case 'nir_hr':
        return 'NIR (hr)';

      case 'pan':
        return this.blahService.blah('task.observations.blackAndWhite') + ' (hr)'

      default:
        return type + this.blahService.blah('task.observations.imageTypeSuffix');
    }
  }

  public imageArchiveVisibleChanged() {
    this.imageArchiveVisible = !this.imageArchiveVisible;
    this.cdRef.markForCheck();
  }

  public imageArchiveStatusChanged(image: ObservationAttachment) {
    image.Archived = !image.Archived;
    this.observationService.setImageArchivedImage(this.selectedObservation.Id, image.Id, image.Archived).subscribe(() => {
      this.cdRef.markForCheck();
    });


  }

  public updateObservationInterpreted(): void {
    if (this.selectedObservation.UpdateObservationInterpretedAllowed) {
      // We omit the time part when changed by user manually since user cannot set the time.
      const observationInterpreted = this.selectedObservation.ObservationInterpreted;
      const offset = observationInterpreted.substring(observationInterpreted.indexOf('.'), observationInterpreted.length);

      this.selectedObservation.ObservationInterpreted =
        `${observationInterpreted.substring(0, observationInterpreted.indexOf('T'))}T22:00:00${offset}`;

      this.save();

    }
  }

  public save() {
    this.observationService.update(this.observationId, this.selectedObservation.ObservationInterpreted, this.selectedObservation.Validity)
      .subscribe({
        next: res => {
          this.updated.next(res);
        }
      });
  }


}
